import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Button,
  Typography,
  Grid,
  Chip,
  Divider,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import DrawIcon from '@mui/icons-material/Draw';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ThumbUpAltTwoToneIcon from '@mui/icons-material/ThumbUpAltTwoTone';
import ThumbDownAltTwoToneIcon from '@mui/icons-material/ThumbDownAltTwoTone';
import DescriptionIcon from '@mui/icons-material/Description';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import API_CONFIG from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch, openAuthenticatedUpload } from '../api/client';
import AtualizarProcessoModal from '../components/contratos/AtualizarProcessoModal';
import ContratosModuloHeader from '../components/contratos/ContratosModuloHeader';
import AtividadeRowActions from '../components/contratos/AtividadeRowActions';
import ProcessoHistoricoTimeline from '../components/contratos/ProcessoHistoricoTimeline';
import VisaoRestritaBanner from '../components/VisaoRestritaBanner';
import { exportAtividadesExcel, exportAtividadesPdf } from './contratos/utils/atividadesExport';
import { withSetorScope, isFiscalSetorial, getFiscalSetor } from '../utils/dataIsolation';

const DESTINOS_OPTIONS = [
  { value: 'GERENCIA_ADMINISTRATIVA', label: 'Diretoria Executiva' },
  { value: 'SETOR_CONTRATOS', label: 'Setor de Contratos' },
  { value: 'FISCAL', label: 'Fiscal Responsável' },
  { value: 'FISCAL_FINANCEIRO', label: 'Fiscal Financeiro' },
  { value: 'FISCAL_GERENCIA_ADM', label: 'Fiscal - Diretoria Executiva' },
  { value: 'CORPORATIVO', label: 'Corporativo' },
  { value: 'JURIDICO', label: 'Jurídico' },
  { value: 'COMPRAS', label: 'Compras' },
  { value: 'PLANEJAMENTO', label: 'Planejamento' }
];

const PRIVILEGED_DESTINOS = ['GERENCIA_ADMINISTRATIVA', 'SETOR_CONTRATOS'];

const STATUS_LABELS = {
  PENDENTE: 'Pendente',
  AUTORIZADO: 'Autorizado',
  NEGADO: 'Negado'
};

const STATUS_COLORS = {
  PENDENTE: { color: '#ef6c00', background: 'rgba(239,108,0,0.12)' },
  AUTORIZADO: { color: '#2e7d32', background: 'rgba(46,125,50,0.12)' },
  NEGADO: { color: '#c62828', background: 'rgba(198,40,40,0.12)' }
};

const DESTINO_LABEL = DESTINOS_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const normalizarChave = (valor) => {
  if (!valor || typeof valor !== 'string') return '';
  return valor
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
};

const SOLICITACAO_TIPO_LABELS = {
  CI: 'Comunicado Interno',
  TR: 'Termo de Referência',
  NOTIFICACAO: 'Notificação'
};

const CI_TIPO_LABELS = {
  ADITIVO: 'Aditivo',
  CONTRATACAO: 'Contratação'
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  })}`;
};

export default function ContratosAtividadesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [atividades, setAtividades] = useState([]);
  const [assinaturasPorProcesso, setAssinaturasPorProcesso] = useState({});
  const [highlightKey, setHighlightKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFiltro, setStatusFiltro] = useState('TODOS');
  const [destinoFiltro, setDestinoFiltro] = useState('');
  const [situacaoFiltro, setSituacaoFiltro] = useState('TODOS');
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [processingIds, setProcessingIds] = useState([]);
  const [decisaoDialogOpen, setDecisaoDialogOpen] = useState(false);
  const [decisaoDialogAtividade, setDecisaoDialogAtividade] = useState(null);
  const [decisaoDialogAcao, setDecisaoDialogAcao] = useState(null);
  const [decisaoArquivo, setDecisaoArquivo] = useState(null);
  const [decisaoObservacao, setDecisaoObservacao] = useState('');
  const [historicoProcessoId, setHistoricoProcessoId] = useState(null);
  const [timelineHistorico, setTimelineHistorico] = useState(null);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [atualizarProcessoOpen, setAtualizarProcessoOpen] = useState(false);
  const [atualizarProcessoId, setAtualizarProcessoId] = useState(null);
  const [modalProcessoPagamentoOpen, setModalProcessoPagamentoOpen] = useState(false);
  const [processoPagamentoSelecionado, setProcessoPagamentoSelecionado] = useState(null);
  const [pdfProcessoPagamentoBlobUrl, setPdfProcessoPagamentoBlobUrl] = useState(null);
  const [loadingPdfProcessoPagamento, setLoadingPdfProcessoPagamento] = useState(false);
  const [decisaoProcessoPagamentoEnviando, setDecisaoProcessoPagamentoEnviando] = useState(false);
  const [observacaoProcessoPagamento, setObservacaoProcessoPagamento] = useState('');

  const { user, isAdmin, isFiscal, fiscalSetor, unidadeSelecionada, rbacRole, hasPermission } = useAuth();
  const podeAssinaturas = Boolean(hasPermission?.('Assinaturas'));
  const usuarioEmail = user?.email || '';
  const usuarioTipo = user?.tipo || '';
  const usuarioSetor = user?.setor || '';
  const scopeOpts = { user, rbacRole, fiscalSetor };
  const setorCodigo = useMemo(() => normalizarChave(usuarioSetor), [usuarioSetor]);
  const tipoCodigo = useMemo(() => normalizarChave(usuarioTipo), [usuarioTipo]);
  const podeDecidirAtividades = useMemo(() => {
    if (isAdmin) return true;
    if (tipoCodigo.includes('GERENCI')) return true;
    return PRIVILEGED_DESTINOS.includes(setorCodigo);
  }, [isAdmin, tipoCodigo, setorCodigo]);

  const normalizarAtividade = useCallback((item) => {
    if (!item) return null;
    const destinoLabel =
      item.destinoLabel ||
      item.destino ||
      (item.destinoCodigo ? DESTINO_LABEL[item.destinoCodigo] : null) ||
      item.destino ||
      '';
    const statusLabel = STATUS_LABELS[item.status] || item.status;
    const solicitacaoTipoLabel = item.solicitacaoTipo
      ? SOLICITACAO_TIPO_LABELS[item.solicitacaoTipo] || item.solicitacaoTipo
      : null;
    const ciTipoLabel = item.ciTipo ? CI_TIPO_LABELS[item.ciTipo] || item.ciTipo : null;

    return {
      ...item,
      destinoLabel,
      statusLabel,
      solicitacaoTipoLabel,
      ciTipoLabel
    };
  }, []);

  const resumoCards = useMemo(() => {
    const total = atividades.length;
    const pendentes = atividades.filter((item) => item.status === 'PENDENTE').length;
    const autorizadas = atividades.filter((item) => item.status === 'AUTORIZADO').length;
    const negadas = atividades.filter((item) => item.status === 'NEGADO').length;
    const processosIdsConcluidos = [...new Set(
      atividades.filter((a) => a.processoStatus === 'CONCLUIDO' && a.processoId).map((a) => a.processoId)
    )];
    const concluidos = processosIdsConcluidos.length;

    return [
      {
        titulo: 'Atividades Pendentes',
        valor: pendentes,
        icone: <PendingActionsIcon sx={{ fontSize: 26 }} />,
        cor: '#ef6c00',
        fundo: 'linear-gradient(135deg, rgba(239,108,0,0.12) 0%, rgba(255,152,0,0.18) 100%)'
      },
      {
        titulo: 'Atividades Autorizadas',
        valor: autorizadas,
        icone: <DoneAllIcon sx={{ fontSize: 26 }} />,
        cor: '#2e7d32',
        fundo: 'linear-gradient(135deg, rgba(46,125,50,0.12) 0%, rgba(102,187,106,0.18) 100%)'
      },
      {
        titulo: 'Atividades Negadas',
        valor: negadas,
        icone: <AccessTimeIcon sx={{ fontSize: 26 }} />,
        cor: '#c62828',
        fundo: 'linear-gradient(135deg, rgba(198,40,40,0.12) 0%, rgba(239,83,80,0.18) 100%)'
      },
      {
        titulo: 'Processos Concluídos',
        valor: concluidos,
        icone: <CheckCircleIcon sx={{ fontSize: 26 }} />,
        cor: '#2e7d32',
        fundo: 'linear-gradient(135deg, rgba(46,125,50,0.12) 0%, rgba(102,187,106,0.18) 100%)'
      },
      {
        titulo: 'Total Registradas',
        valor: total,
        icone: <AssignmentTurnedInIcon sx={{ fontSize: 26 }} />,
        cor: '#1565c0',
        fundo: 'linear-gradient(135deg, rgba(21,101,192,0.12) 0%, rgba(33,150,243,0.18) 100%)'
      }
    ];
  }, [atividades]);

  const loadAtividades = useCallback(async () => {
    if (!usuarioEmail && !usuarioTipo && !usuarioSetor) {
      setAtividades([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFiltro !== 'TODOS') {
        params.append('status', statusFiltro);
      }
      if (destinoFiltro) {
        params.append('destino', destinoFiltro);
      }
      if (usuarioEmail) {
        params.append('usuarioEmail', usuarioEmail);
      }
      if (usuarioTipo) {
        params.append('usuarioTipo', usuarioTipo);
      }
      if (usuarioSetor) {
        params.append('usuarioSetor', usuarioSetor);
      }
      if (unidadeSelecionada?.codigo) {
        params.append('unidade', unidadeSelecionada.codigo);
      }
      const setorScope = getFiscalSetor(user, fiscalSetor);
      if (isFiscalSetorial(user, rbacRole) && setorScope) {
        params.append('setor', setorScope);
      }

      const pathAtiv = withSetorScope(
        `/api/contratos/atividades?${params.toString()}`,
        scopeOpts
      );
      const procParams = new URLSearchParams();
      if (statusFiltro !== 'TODOS') procParams.set('status', statusFiltro);
      if (unidadeSelecionada?.codigo) procParams.set('unidade', unidadeSelecionada.codigo);
      if (isFiscalSetorial(user, rbacRole) && setorScope) {
        procParams.set('setor', setorScope);
      }
      const pathProc = withSetorScope(
        `/api/notas-fiscais/atividades-processo-pagamento${procParams.toString() ? `?${procParams.toString()}` : ''}`,
        scopeOpts
      );
      const [responseContratos, responseProcPag] = await Promise.all([
        apiFetch(pathAtiv),
        apiFetch(pathProc)
      ]);

      if (!responseContratos.ok) throw new Error('Erro ao carregar atividades');

      const dataContratos = await responseContratos.json();
      const dataProcPag = responseProcPag.ok ? await responseProcPag.json() : [];
      const normalizadoContratos = Array.isArray(dataContratos)
        ? dataContratos.map((item) => normalizarAtividade(item))
        : [];
      const normalizadoProcPag = Array.isArray(dataProcPag)
        ? dataProcPag.map((row) => ({
            tipo: 'PROCESSO_PAGAMENTO',
            id: row.id,
            processoId: `proc_pag_${row.id}`,
            processoCodigo: row.numero_processo || row.numeroProcesso,
            titulo: `Processo de Pagamento - ${row.numero_processo || row.numeroProcesso}`,
            status: row.status,
            statusLabel: STATUS_LABELS[row.status] || row.status,
            criadoEm: row.criado_em || row.criadoEm,
            notaFiscalIdPrincipal: row.nota_fiscal_id_principal || row.notaFiscalIdPrincipal,
            numeroProcesso: row.numero_processo || row.numeroProcesso,
            empresa: row.empresa,
            setor: row.setor,
            destinoLabel: 'Fiscal',
            assinaturaDocumentoId: row.assinatura_documento_id || row.assinaturaDocumentoId || null,
            avaliacaoAssinaturaDocumentoId:
              row.avaliacao_assinatura_documento_id || row.avaliacaoAssinaturaDocumentoId || null,
          }))
        : [];
      setAtividades([...normalizadoContratos, ...normalizadoProcPag]);
      if (podeAssinaturas && unidadeSelecionada?.codigo) {
        try {
          const qsAss = new URLSearchParams({ unidade: unidadeSelecionada.codigo });
          const resAss = await apiFetch(`/api/assinaturas/documentos?${qsAss.toString()}`);
          if (resAss.ok) {
            const docs = await resAss.json();
            const mapa = {};
            (Array.isArray(docs) ? docs : []).forEach((d) => {
              if (d?.processoId != null) mapa[String(d.processoId)] = d;
            });
            setAssinaturasPorProcesso(mapa);
          }
        } catch (e) {
          console.warn('Vínculos de assinatura indisponíveis', e);
        }
      } else {
        setAssinaturasPorProcesso({});
      }
    } catch (error) {
      console.error('Erro ao carregar atividades:', error);
      setAtividades([]);
      setSnackbar({ open: true, message: error.message || 'Erro ao carregar atividades', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [statusFiltro, destinoFiltro, usuarioEmail, usuarioTipo, usuarioSetor, unidadeSelecionada?.codigo, normalizarAtividade, user, rbacRole, fiscalSetor, podeAssinaturas]);

  useEffect(() => {
    if (!usuarioEmail && !usuarioTipo && !usuarioSetor) return;
    loadAtividades();
  }, [loadAtividades, usuarioEmail, usuarioTipo, usuarioSetor, unidadeSelecionada?.codigo]);

  useEffect(() => {
    const handleRefresh = (event) => {
      if (event?.detail?.source === 'atividades') return;
      loadAtividades();
    };
    window.addEventListener('contratos:refresh', handleRefresh);
    return () => window.removeEventListener('contratos:refresh', handleRefresh);
  }, [loadAtividades]);

  useEffect(() => {
    const highlightAtividade = location.state?.highlightAtividadeId;
    const highlightProcesso = location.state?.highlightProcessoId;
    if (!highlightAtividade && !highlightProcesso) return;
    setHighlightKey(String(highlightProcesso || highlightAtividade));
    if (highlightProcesso) {
      setHistoricoProcessoId(highlightProcesso);
    }
    if (location.state?.statusFiltro) {
      setStatusFiltro(location.state.statusFiltro);
    } else {
      setStatusFiltro('PENDENTE');
    }
  }, [location.state]);

  const atividadesFiltradas = useMemo(() => {
    if (!searchTerm) return atividades;
    const termo = searchTerm.trim().toLowerCase();
    if (!termo) return atividades;
    return atividades.filter((atividade) => {
      const campos = [
        atividade.titulo,
        atividade.destinoLabel,
        atividade.descricao,
        atividade.solicitacaoCodigo,
        atividade.solicitacaoTitulo,
        atividade.processoCodigo,
        atividade.numeroProcesso,
        atividade.empresa
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return campos.includes(termo);
    });
  }, [atividades, searchTerm]);

  const processosAgrupados = useMemo(() => {
    const comProcesso = atividadesFiltradas.filter((a) => a.processoId);
    const byProcesso = {};
    comProcesso.forEach((a) => {
      const key = a.processoId;
      if (!byProcesso[key]) {
        byProcesso[key] = {
          processoId: key,
          processoCodigo: a.processoCodigo,
          titulo: a.solicitacaoTitulo || a.titulo,
          atividades: [],
          tipo: a.tipo,
          setor: a.setor
        };
      }
      byProcesso[key].atividades.push(a);
    });
    return Object.values(byProcesso).map((p) => {
      const pendente = p.atividades.find((a) => a.status === 'PENDENTE');
      const ordenadas = [...p.atividades].sort((x, y) => new Date(y.criadoEm || 0) - new Date(x.criadoEm || 0));
      const processoStatus = p.atividades[0]?.processoStatus || null;
      const processoDocumentoFinalPath = p.atividades[0]?.processoDocumentoFinalPath || null;
      return { ...p, atividadePendente: pendente, ultimaAtividade: ordenadas[0], processoStatus, processoDocumentoFinalPath };
    });
  }, [atividadesFiltradas]);

  const podeDecidirProcessoPagamento = useMemo(() => {
    return isFiscal && !!fiscalSetor && !isAdmin;
  }, [isFiscal, fiscalSetor, isAdmin]);

  const processosAgrupadosFiltrados = useMemo(() => {
    if (situacaoFiltro === 'TODOS') return processosAgrupados;
    if (situacaoFiltro === 'CONCLUIDO') {
      return processosAgrupados.filter((g) => g.processoStatus === 'CONCLUIDO');
    }
    return processosAgrupados.filter((g) => g.processoStatus !== 'CONCLUIDO');
  }, [processosAgrupados, situacaoFiltro]);

  useEffect(() => {
    if (!historicoProcessoId) {
      setTimelineHistorico(null);
      return;
    }
    setLoadingHistorico(true);
    apiFetch(`/api/contratos/processos/${historicoProcessoId}/timeline`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { setTimelineHistorico(d); setLoadingHistorico(false); })
      .catch(() => setLoadingHistorico(false));
  }, [historicoProcessoId]);

  const recarregarHistoricoProcesso = useCallback((processoId) => {
    if (!processoId) return;
    setLoadingHistorico(true);
    apiFetch(`/api/contratos/processos/${processoId}/timeline`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { setTimelineHistorico(d); setLoadingHistorico(false); })
      .catch(() => setLoadingHistorico(false));
  }, []);

  const enviarDecisao = async (atividade, acao, observacao = '', arquivo = null) => {
    if (!podeDecidirAtividades) return;
    setProcessingIds((prev) => [...prev, atividade.id]);
    try {
      const url = `/api/contratos/atividades/${atividade.id}/decisao`;
      const payload = {
        acao,
        usuario: user?.nome || usuarioEmail || 'Sistema',
        usuarioEmail,
        usuarioTipo,
        usuarioSetor,
        observacao: observacao.trim() || ''
      };
      let response;
      if (arquivo && acao === 'AUTORIZAR') {
        const formData = new FormData();
        Object.entries(payload).forEach(([k, v]) => formData.append(k, v != null ? String(v) : ''));
        formData.append('anexo', arquivo);
        response = await apiFetch(url, { method: 'PUT', body: formData });
      } else {
        response = await apiFetch(url, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || 'Erro ao atualizar atividade');
      }

      const dadosNormalizados = normalizarAtividade(data);
      setAtividades((prev) =>
        prev.map((item) => (item.id === atividade.id ? dadosNormalizados || item : item))
      );
      setSnackbar({ open: true, message: `Atividade ${acao === 'AUTORIZAR' ? 'autorizada' : 'negada'} com sucesso!`, severity: 'success' });

      const processoIdAtualizado = dadosNormalizados?.processoId || atividade.processoId || null;
      if (historicoProcessoId && processoIdAtualizado && String(historicoProcessoId) === String(processoIdAtualizado)) {
        recarregarHistoricoProcesso(historicoProcessoId);
      }

      window.dispatchEvent(
        new CustomEvent('contratos:refresh', {
          detail: {
            source: 'atividades',
            tipo: acao === 'AUTORIZAR' ? 'atividade_autorizada' : 'atividade_negada',
            atividadeId: atividade.id,
            processoId: processoIdAtualizado
          }
        })
      );
    } catch (error) {
      console.error('Erro ao atualizar atividade:', error);
      setSnackbar({ open: true, message: error.message || 'Erro ao atualizar atividade', severity: 'error' });
    } finally {
      setProcessingIds((prev) => prev.filter((id) => id !== atividade.id));
    }
  };

  const handleAcaoAtividade = (atividade, acao) => {
    if (!podeDecidirAtividades) return;
    if (acao === 'AUTORIZAR' && atividade.processoId) {
      setAtualizarProcessoId(atividade.processoId);
      setAtualizarProcessoOpen(true);
      return;
    }
    if (acao === 'AUTORIZAR' && atividade.exigeArquivo) {
      setDecisaoDialogAtividade(atividade);
      setDecisaoDialogAcao('AUTORIZAR');
      setDecisaoArquivo(null);
      setDecisaoObservacao('');
      setDecisaoDialogOpen(true);
      return;
    }
    enviarDecisao(atividade, acao, '');
  };

  const handleDecisaoDialogConfirm = () => {
    if (!decisaoDialogAtividade || !decisaoDialogAcao) return;
    if (decisaoDialogAcao === 'AUTORIZAR' && decisaoDialogAtividade.exigeArquivo && !decisaoArquivo) {
      setSnackbar({ open: true, message: 'Envie o arquivo em PDF para autorizar esta etapa.', severity: 'warning' });
      return;
    }
    enviarDecisao(decisaoDialogAtividade, decisaoDialogAcao, decisaoObservacao, decisaoArquivo || undefined);
    setDecisaoDialogOpen(false);
    setDecisaoDialogAtividade(null);
    setDecisaoDialogAcao(null);
    setDecisaoArquivo(null);
    setDecisaoObservacao('');
  };

  const isProcessing = (id) => processingIds.includes(id);

  const abrirModalProcessoPagamento = (grupo) => {
    const atv = grupo.atividadePendente || grupo.ultimaAtividade;
    if (!atv || atv.tipo !== 'PROCESSO_PAGAMENTO') return;
    setProcessoPagamentoSelecionado(atv);
    setObservacaoProcessoPagamento('');
    setModalProcessoPagamentoOpen(true);
    setPdfProcessoPagamentoBlobUrl(null);
  };

  const fecharModalProcessoPagamento = () => {
    setModalProcessoPagamentoOpen(false);
    setProcessoPagamentoSelecionado(null);
    setObservacaoProcessoPagamento('');
    setPdfProcessoPagamentoBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  useEffect(() => {
    if (!processoPagamentoSelecionado?.notaFiscalIdPrincipal || !modalProcessoPagamentoOpen) return;
    setLoadingPdfProcessoPagamento(true);
    apiFetch(`/api/notas-fiscais/${processoPagamentoSelecionado.notaFiscalIdPrincipal}/pdf-consolidado`)
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (blob) setPdfProcessoPagamentoBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
      })
      .finally(() => setLoadingPdfProcessoPagamento(false));
  }, [processoPagamentoSelecionado?.notaFiscalIdPrincipal, modalProcessoPagamentoOpen]);

  const enviarDecisaoProcessoPagamento = async (acao) => {
    if (!processoPagamentoSelecionado || !podeDecidirProcessoPagamento) return;
    const obs = String(observacaoProcessoPagamento || '').trim();
    if (!obs) {
      setSnackbar({
        open: true,
        message: 'Informe a observação para registrar a auditoria da decisão.',
        severity: 'warning'
      });
      return;
    }
    setDecisaoProcessoPagamentoEnviando(true);
    try {
      const res = await apiFetch(`/api/notas-fiscais/atividades/${processoPagamentoSelecionado.id}/decisao`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ acao, usuarioEmail: user?.email, observacao: obs })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Erro ao registrar decisão');
      setAtividades((prev) => prev.map((a) => (a.id === processoPagamentoSelecionado.id ? { ...a, status: acao === 'AUTORIZAR' ? 'AUTORIZADO' : 'NEGADO' } : a)));
      setSnackbar({ open: true, message: data.message || (acao === 'AUTORIZAR' ? 'Autorizado.' : 'Negado.'), severity: 'success' });
      fecharModalProcessoPagamento();
      loadAtividades();
      window.dispatchEvent(
        new CustomEvent('contratos:refresh', {
          detail: {
            source: 'atividades',
            tipo: acao === 'AUTORIZAR' ? 'atividade_autorizada' : 'atividade_negada',
            atividadeId: processoPagamentoSelecionado.id
          }
        })
      );
      if (acao === 'AUTORIZAR') {
        const notaId = data.notaFiscalId || processoPagamentoSelecionado.notaFiscalIdPrincipal;
        const pagDocId =
          data.assinaturaDocumentoId || processoPagamentoSelecionado.assinaturaDocumentoId;
        const avalDocId =
          data.avaliacaoAssinaturaDocumentoId ||
          processoPagamentoSelecionado.avaliacaoAssinaturaDocumentoId;
        if (data.precisaAvaliacao !== false) {
          navigate('/notas-fiscais', {
            state: {
              avaliarNotaId: notaId,
              pagamentoAssinaturaDocumentoId: pagDocId,
              avaliacaoAssinaturaDocumentoId: avalDocId,
            },
          });
        } else if (avalDocId) {
          navigate(`/assinaturas/documentos/${avalDocId}`, {
            state: { proximoAssinaturaDocumentoId: pagDocId },
          });
        } else if (pagDocId) {
          navigate(`/assinaturas/documentos/${pagDocId}`);
        } else {
          navigate('/notas-fiscais', { state: { avaliarNotaId: notaId } });
        }
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Erro ao registrar decisão', severity: 'error' });
    } finally {
      setDecisaoProcessoPagamentoEnviando(false);
    }
  };

  const handleEnviarAssinatura = (grupo, atv) => {
    if (!podeAssinaturas) {
      setSnackbar({ open: true, message: 'Sem permissão no módulo Assinaturas.', severity: 'warning' });
      return;
    }
    const docPath = grupo.processoDocumentoFinalPath || atv?.processoDocumentoFinalPath;
    const arquivoUrl = docPath
      ? `${API_CONFIG.BACKEND_URL}/${String(docPath).replace(/\\/g, '/').replace(/^\/+/, '')}`
      : (atv?.solicitacaoId
        ? `${API_CONFIG.BACKEND_URL}/api/contratos/solicitacoes/${atv.solicitacaoId}/pdf-mesclado`
        : null);

    const participantesPrefill = [];
    const dest = atv?.solicitacaoDestinatario;
    if (dest?.nome && dest?.email) {
      participantesPrefill.push({ nome: dest.nome, email: dest.email, cpf: '' });
    }
    if (atv?.responsavelNome && atv?.responsavelEmail) {
      const jaTem = participantesPrefill.some(
        (p) => String(p.email).toLowerCase() === String(atv.responsavelEmail).toLowerCase()
      );
      if (!jaTem) {
        participantesPrefill.push({
          nome: atv.responsavelNome,
          email: atv.responsavelEmail,
          cpf: '',
        });
      }
    }
    if (user?.nome && user?.email) {
      const jaTem = participantesPrefill.some(
        (p) => String(p.email).toLowerCase() === String(user.email).toLowerCase()
      );
      if (!jaTem) {
        participantesPrefill.push({ nome: user.nome, email: user.email, cpf: '' });
      }
    }

    navigate('/assinaturas/novo', {
      state: {
        fromContratos: true,
        processoId: Number.isFinite(Number(grupo.processoId)) ? Number(grupo.processoId) : grupo.processoId,
        atividadeId: atv?.id || null,
        solicitacaoId: atv?.solicitacaoId || null,
        titulo: `Assinatura — ${grupo.processoCodigo || atv?.titulo || 'Processo'}`,
        descricao: atv?.titulo || grupo.titulo || '',
        arquivoUrl,
        processoCodigo: grupo.processoCodigo || null,
        participantes: participantesPrefill.length ? participantesPrefill : undefined,
        tipoDocumento: (() => {
          const tf = String(atv?.processoTipoFluxo || grupo.processoTipoFluxo || '').toUpperCase();
          if (tf === 'ADITIVO') return 'ADITIVO';
          if (tf === 'MINUTA') return 'MINUTA';
          if (tf === 'PAGAMENTO') return 'PROCESSO_PAGAMENTO';
          return 'CONTRATACAO';
        })(),
      }
    });
  };

  return (
    <Box sx={{ p: 2 }}>
      <VisaoRestritaBanner />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <ContratosModuloHeader />
      </Box>
      <Dialog
        open={Boolean(historicoProcessoId)}
        onClose={() => setHistoricoProcessoId(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 40%)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background:
              'linear-gradient(135deg, rgba(15,23,42,0.94) 0%, rgba(30,64,175,0.88) 60%, rgba(13,148,136,0.8) 100%)',
            color: '#e2e8f0',
            py: 1.75,
          }}
        >
          <Box>
            <Typography variant="overline" sx={{ color: '#99f6e4', fontWeight: 800, letterSpacing: 1 }}>
              Timeline do fluxo
            </Typography>
            <Typography variant="h6" fontWeight={800} sx={{ color: '#fff', lineHeight: 1.2 }}>
              Histórico do processo
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setHistoricoProcessoId(null)} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'rgba(248,250,252,0.65)' }}>
          {loadingHistorico ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : timelineHistorico?.timeline ? (
            <ProcessoHistoricoTimeline
              timeline={timelineHistorico.timeline}
              tempoTotalDias={timelineHistorico.tempoTotalDias}
              tempoTotalHoras={timelineHistorico.tempoTotalHoras}
            />
          ) : (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              Nenhum histórico encontrado.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      <AtualizarProcessoModal
        open={atualizarProcessoOpen}
        onClose={() => { setAtualizarProcessoOpen(false); setAtualizarProcessoId(null); loadAtividades(); }}
        processoId={atualizarProcessoId}
        user={user}
        usuarioEmail={usuarioEmail}
        usuarioTipo={usuarioTipo}
        usuarioSetor={usuarioSetor}
        onSuccess={({ data, error }) => {
          if (error) setSnackbar({ open: true, message: error, severity: 'error' });
          else {
            const msg = data?.exigeAssinaturaDigital
              ? 'Processo atualizado. Esta etapa exige assinatura digital.'
              : 'Processo atualizado com sucesso!';
            setSnackbar({ open: true, message: msg, severity: 'success' });
            loadAtividades();
            if (atualizarProcessoId) recarregarHistoricoProcesso(atualizarProcessoId);
            window.dispatchEvent(
              new CustomEvent('contratos:refresh', {
                detail: {
                  source: 'atividades',
                  tipo: 'atividade_autorizada',
                  processoId: atualizarProcessoId
                }
              })
            );
            if (data?.exigeAssinaturaDigital) {
              if (data.assinaturaDocumentoId) {
                navigate(`/assinaturas/documentos/${data.assinaturaDocumentoId}`);
              } else {
                const tipoFluxo = String(data.tipoFluxo || 'CONTRATACAO').toUpperCase();
                navigate('/assinaturas/novo', {
                  state: {
                    fromContratos: true,
                    processoId: data.id || atualizarProcessoId,
                    titulo: `Assinatura — ${data.codigo || 'Processo'}`,
                    processoCodigo: data.codigo || null,
                    tipoDocumento: tipoFluxo === 'ADITIVO' ? 'ADITIVO' : tipoFluxo === 'MINUTA' ? 'MINUTA' : 'CONTRATACAO',
                    arquivoUrl: data.documentoFinalUrl || null,
                  },
                });
              }
            }
          }
        }}
        podeGerenciarProcessos={podeDecidirAtividades}
        podePularEtapas={isAdmin}
      />

      <Dialog open={modalProcessoPagamentoOpen} onClose={fecharModalProcessoPagamento} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', color: 'white' }}>
          <Typography variant="h6">Processo de Pagamento – {processoPagamentoSelecionado?.processoCodigo || processoPagamentoSelecionado?.numeroProcesso}</Typography>
          <IconButton size="small" onClick={fecharModalProcessoPagamento} sx={{ color: 'white' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Visualize o PDF consolidado dos documentos anexados. {podeDecidirProcessoPagamento && processoPagamentoSelecionado?.status === 'PENDENTE' && 'Autorize ou negue o processo abaixo.'}
          </Typography>
          <Box sx={{ height: 420, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {loadingPdfProcessoPagamento && <CircularProgress />}
            {!loadingPdfProcessoPagamento && pdfProcessoPagamentoBlobUrl && (
              <iframe title="PDF consolidado" src={pdfProcessoPagamentoBlobUrl} style={{ width: '100%', height: '100%', border: 'none' }} />
            )}
            {!loadingPdfProcessoPagamento && !pdfProcessoPagamentoBlobUrl && processoPagamentoSelecionado && (
              <Typography variant="body2" color="text.secondary">PDF não disponível ou em carregamento.</Typography>
            )}
          </Box>
          {podeDecidirProcessoPagamento && processoPagamentoSelecionado?.status === 'PENDENTE' && (
            <TextField
              label="Observação (auditoria)"
              placeholder="Descreva o motivo da autorização ou negativa…"
              value={observacaoProcessoPagamento}
              onChange={(e) => setObservacaoProcessoPagamento(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              required
              sx={{ mt: 2 }}
              helperText="Obrigatória para registrar quem decidiu e por quê (trilha de auditoria)."
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={fecharModalProcessoPagamento}>Fechar</Button>
          {processoPagamentoSelecionado?.status === 'EM_ANDAMENTO' &&
            (processoPagamentoSelecionado?.avaliacaoAssinaturaDocumentoId ||
              processoPagamentoSelecionado?.assinaturaDocumentoId) && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<DrawIcon />}
                onClick={() => {
                  const avalId = processoPagamentoSelecionado.avaliacaoAssinaturaDocumentoId;
                  const pagId = processoPagamentoSelecionado.assinaturaDocumentoId;
                  fecharModalProcessoPagamento();
                  if (avalId) {
                    navigate(`/assinaturas/documentos/${avalId}`, {
                      state: { proximoAssinaturaDocumentoId: pagId },
                    });
                  } else if (pagId) {
                    navigate(`/assinaturas/documentos/${pagId}`);
                  }
                }}
              >
                Continuar assinatura digital
              </Button>
            )}
          {podeDecidirProcessoPagamento && processoPagamentoSelecionado?.status === 'PENDENTE' && (
            <>
              <Button
                variant="outlined"
                color="error"
                startIcon={<ThumbDownAltTwoToneIcon />}
                disabled={decisaoProcessoPagamentoEnviando || !String(observacaoProcessoPagamento || '').trim()}
                onClick={() => enviarDecisaoProcessoPagamento('NEGAR')}
              >
                Negar
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={decisaoProcessoPagamentoEnviando ? <CircularProgress size={18} color="inherit" /> : <ThumbUpAltTwoToneIcon />}
                disabled={decisaoProcessoPagamentoEnviando || !String(observacaoProcessoPagamento || '').trim()}
                onClick={() => enviarDecisaoProcessoPagamento('AUTORIZAR')}
              >
                {decisaoProcessoPagamentoEnviando ? 'Enviando…' : 'Autorizar'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={decisaoDialogOpen} onClose={() => setDecisaoDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {decisaoDialogAcao === 'AUTORIZAR' && decisaoDialogAtividade?.exigeArquivo
            ? 'Enviar arquivo para autorizar'
            : 'Confirmar decisão'}
          <IconButton size="small" onClick={() => setDecisaoDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {decisaoDialogAtividade?.exigeArquivo && decisaoDialogAcao === 'AUTORIZAR' && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Esta etapa exige o envio de um arquivo (PDF) para autorizar e passar para a próxima. Anexe o documento abaixo.
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFileIcon />}
                fullWidth
                sx={{ py: 1.5 }}
              >
                {decisaoArquivo ? decisaoArquivo.name : 'Selecionar PDF'}
                <input
                  type="file"
                  hidden
                  accept=".pdf,application/pdf"
                  onChange={(e) => setDecisaoArquivo(e.target.files?.[0] || null)}
                />
              </Button>
              <TextField
                label="Observação (opcional)"
                value={decisaoObservacao}
                onChange={(e) => setDecisaoObservacao(e.target.value)}
                multiline
                rows={2}
                fullWidth
                size="small"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDecisaoDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleDecisaoDialogConfirm}
            disabled={
              (decisaoDialogAcao === 'AUTORIZAR' && decisaoDialogAtividade?.exigeArquivo && !decisaoArquivo) ||
              processingIds.includes(decisaoDialogAtividade?.id)
            }
          >
            {processingIds.includes(decisaoDialogAtividade?.id) ? 'Enviando…' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 4,
          mb: 3,
          background: 'linear-gradient(135deg, rgba(21,101,192,0.16) 0%, rgba(144,202,249,0.22) 100%)',
          border: '1px solid rgba(21,101,192,0.12)',
          boxShadow: '0 12px 26px rgba(21,101,192,0.15)',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2
        }}
      >
        <Typography variant="h6" fontWeight={800} color="primary">
          Atividades
        </Typography>

        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<DescriptionIcon />}
            onClick={() => navigate('/contratos/solicitacoes')}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Solicitações
          </Button>
          <Button
            variant="outlined"
            startIcon={<WorkHistoryIcon />}
            onClick={() => navigate('/contratos/processo-contratacao')}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Processos
          </Button>
          {podeAssinaturas && (
            <Button
              variant="outlined"
              startIcon={<DrawIcon />}
              onClick={() => navigate('/assinaturas/documentos')}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Assinaturas
            </Button>
          )}
          <Tooltip title="Atualizar lista">
            <span>
              <IconButton color="primary" onClick={loadAtividades} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          mb: 3,
          border: '1px solid rgba(21,101,192,0.08)',
          background: 'rgba(255,255,255,0.92)'
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={800} color="primary">
            Panorama das Atividades
          </Typography>
          <Chip
            icon={<AssignmentTurnedInIcon fontSize="small" />}
            label={`${atividades.length} atividade(s) monitoradas`}
            variant="outlined"
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              color: '#1565c0',
              borderColor: 'rgba(21,101,192,0.4)'
            }}
          />
        </Box>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2.5}>
          {resumoCards.map((card) => (
            <Grid item xs={12} sm={6} md={3} key={card.titulo}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: card.fundo,
                  border: `1px solid ${card.cor}33`
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: card.cor, fontWeight: 700 }}>
                      {card.titulo}
                    </Typography>
                    <Typography variant="h4" fontWeight={800} sx={{ color: '#0d1b2a' }}>
                      {card.valor}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: `${card.cor}1a`,
                      color: card.cor,
                      display: 'flex'
                    }}
                  >
                    {card.icone}
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid rgba(21,101,192,0.08)',
          background: 'rgba(255,255,255,0.95)'
        }}
      >
        <Box
          sx={{
            p: 3,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            background: 'linear-gradient(135deg, rgba(21,101,192,0.06) 0%, rgba(21,101,192,0.02) 100%)',
            borderBottom: '1px solid rgba(21,101,192,0.08)',
            borderRadius: 'inherit'
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={800} color="primary">
              Lista de atividades
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Acompanhe as responsabilidades e registros de cada etapa do processo de contratação.
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 180, backgroundColor: 'white', borderRadius: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFiltro}
                label="Status"
                onChange={(event) => setStatusFiltro(event.target.value)}
              >
                <MenuItem value="TODOS">Todos</MenuItem>
                <MenuItem value="PENDENTE">Pendente</MenuItem>
                <MenuItem value="AUTORIZADO">Autorizado</MenuItem>
                <MenuItem value="NEGADO">Negado</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200, backgroundColor: 'white', borderRadius: 2 }}>
              <InputLabel>Situação do processo</InputLabel>
              <Select
                value={situacaoFiltro}
                label="Situação do processo"
                onChange={(event) => setSituacaoFiltro(event.target.value)}
              >
                <MenuItem value="TODOS">Todos</MenuItem>
                <MenuItem value="EM_ANDAMENTO">Em andamento</MenuItem>
                <MenuItem value="CONCLUIDO">Concluídos</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200, backgroundColor: 'white', borderRadius: 2 }}>
              <InputLabel>Destino</InputLabel>
              <Select
                value={destinoFiltro}
                label="Destino"
                onChange={(event) => setDestinoFiltro(event.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {DESTINOS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              placeholder="Pesquisar..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
              sx={{ minWidth: { xs: '100%', md: 260 }, backgroundColor: 'white', borderRadius: 2 }}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              disabled={!atividadesFiltradas.length}
              onClick={() => {
                const n = exportAtividadesPdf(atividadesFiltradas, {
                  label: 'Lista',
                  unidade: unidadeSelecionada?.codigo || '',
                });
                setSnackbar({
                  open: true,
                  message: n ? `PDF gerado (${n} registro(s)).` : 'Nenhum registro.',
                  severity: n ? 'success' : 'warning',
                });
              }}
              sx={{ textTransform: 'none' }}
            >
              PDF
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              disabled={!atividadesFiltradas.length}
              onClick={() => {
                const n = exportAtividadesExcel(atividadesFiltradas, { label: 'Lista' });
                setSnackbar({
                  open: true,
                  message: n ? `Excel gerado (${n} registro(s)).` : 'Nenhum registro.',
                  severity: n ? 'success' : 'warning',
                });
              }}
              sx={{ textTransform: 'none' }}
            >
              Excel
            </Button>
          </Stack>
        </Box>

        <Box sx={{ p: 3 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={220}>
              <CircularProgress />
            </Box>
          ) : processosAgrupadosFiltrados.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: '1px dashed rgba(21,101,192,0.2)',
                background: 'rgba(247,249,255,0.9)',
                textAlign: 'center'
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} color="primary">
                Nenhum processo com atividades
              </Typography>
              <Typography variant="body2" color="text.secondary">
                As atividades vinculadas a processos aparecerão aqui agrupadas.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={2}>
              {processosAgrupadosFiltrados.map((grupo) => {
                const atv = grupo.atividadePendente || grupo.ultimaAtividade;
                if (!atv) return null;
                const processoConcluido = grupo.processoStatus === 'CONCLUIDO';
                const statusParaExibir = processoConcluido ? 'CONCLUIDO' : atv.status;
                const statusLabelParaExibir = processoConcluido ? 'Concluído' : (atv.statusLabel || atv.status);
                const statusInfo = STATUS_COLORS[statusParaExibir] || { color: '#2e7d32', background: 'rgba(46,125,50,0.12)' };
                const isPendente = !!grupo.atividadePendente;
                const documentoFinalUrl = grupo.processoDocumentoFinalPath
                  ? `${API_CONFIG.BACKEND_URL}/${(grupo.processoDocumentoFinalPath || '').replace(/\\/g, '/')}`
                  : null;

                const isProcessoPagamento = grupo.tipo === 'PROCESSO_PAGAMENTO';
                const podeDecidirProcPag = isProcessoPagamento && podeDecidirProcessoPagamento && isFiscal && grupo.setor === fiscalSetor;

                const docAssinaturaPag =
                  atv.assinaturaDocumentoId ||
                  atv.avaliacaoAssinaturaDocumentoId ||
                  assinaturasPorProcesso[String(grupo.processoId)]?.id ||
                  null;
                const docAssinatura = docAssinaturaPag
                  ? { id: docAssinaturaPag }
                  : assinaturasPorProcesso[String(grupo.processoId)];
                const isHighlighted =
                  highlightKey &&
                  (String(grupo.processoId) === String(highlightKey) ||
                    grupo.atividades.some((a) => String(a.id) === String(highlightKey)));

                const irParaAssinaturaPagamento = () => {
                  const avalId = atv.avaliacaoAssinaturaDocumentoId;
                  const pagId = atv.assinaturaDocumentoId || docAssinatura?.id;
                  if (avalId && atv.status === 'EM_ANDAMENTO') {
                    navigate(`/assinaturas/documentos/${avalId}`, {
                      state: { proximoAssinaturaDocumentoId: pagId },
                    });
                    return;
                  }
                  if (pagId) {
                    navigate(`/assinaturas/documentos/${pagId}`);
                    return;
                  }
                  setSnackbar({
                    open: true,
                    message: 'Documento de assinatura ainda não vinculado a este processo.',
                    severity: 'warning',
                  });
                };

                return (
                  <Paper
                    key={grupo.processoId}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: isHighlighted ? '2px solid #1565c0' : '1px solid rgba(21,101,192,0.12)',
                      boxShadow: isHighlighted ? '0 0 0 3px rgba(21,101,192,0.18)' : undefined,
                      background: isProcessoPagamento ? 'rgba(255,193,7,0.06)' : 'rgba(255,255,255,0.95)',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                    onClick={() => (isProcessoPagamento ? abrirModalProcessoPagamento(grupo) : setHistoricoProcessoId(grupo.processoId))}
                  >
                    <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between" gap={2}>
                      <Box flex={1} minWidth={0}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                          <Chip
                            size="small"
                            label={isProcessoPagamento ? 'Pagamento' : 'Contratação'}
                            color={isProcessoPagamento ? 'warning' : 'primary'}
                            variant="outlined"
                            sx={{ fontWeight: 700 }}
                          />
                          {docAssinatura && (
                            <Chip size="small" icon={<DrawIcon sx={{ fontSize: 14 }} />} label="Assinatura vinculada" color="success" variant="outlined" />
                          )}
                        </Stack>
                        <Typography variant="h6" fontWeight={700} color="primary">
                          {isProcessoPagamento ? 'Processo de Pagamento' : (grupo.processoCodigo || `Processo ${grupo.processoId}`)}
                        </Typography>
                        {isProcessoPagamento && (
                          <Typography variant="body2" color="text.secondary">
                            {grupo.processoCodigo} · {grupo.atividades[0]?.empresa || ''}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {atv.destinoLabel && `${atv.destinoLabel} · `}
                          Status: {statusLabelParaExibir}
                          {atv.decisaoUsuario && ` · Última decisão: ${atv.decisaoUsuario} ${atv.decisaoData ? formatDateTime(atv.decisaoData) : ''}`}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1} onClick={(e) => e.stopPropagation()}>
                        <Chip
                          label={statusLabelParaExibir}
                          size="small"
                          sx={{ fontWeight: 700, color: statusInfo.color, backgroundColor: statusInfo.background }}
                        />
                        <AtividadeRowActions
                          atividade={atv}
                          processoConcluido={processoConcluido}
                          isPendente={isPendente}
                          podeDecidir={isProcessoPagamento ? podeDecidirProcPag : podeDecidirAtividades}
                          processando={isProcessing(atv.id)}
                          podeAssinaturas={podeAssinaturas}
                          assinaturaDocumentoId={
                            atv.assinaturaDocumentoId ||
                            atv.avaliacaoAssinaturaDocumentoId ||
                            docAssinatura?.id ||
                            null
                          }
                          documentoFinalUrl={documentoFinalUrl}
                          isProcessoPagamento={isProcessoPagamento}
                          onAutorizar={() => handleAcaoAtividade(atv, 'AUTORIZAR')}
                          onNegar={() => handleAcaoAtividade(atv, 'NEGAR')}
                          onHistorico={() => setHistoricoProcessoId(grupo.processoId)}
                          onAbrirProcesso={() => navigate('/contratos/processo-contratacao', { state: { highlightProcessoId: grupo.processoId } })}
                          onBaixarPdf={() => openAuthenticatedUpload(documentoFinalUrl)}
                          onEnviarAssinatura={() => handleEnviarAssinatura(grupo, atv)}
                          onVerAssinatura={() =>
                            isProcessoPagamento
                              ? irParaAssinaturaPagamento()
                              : navigate(`/assinaturas/documentos/${docAssinatura.id}`)
                          }
                        />
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}


